import { Button, Modal } from 'antd';
import React, { useEffect, useState } from 'react';
import { isChrome, isFirefox } from 'react-device-detect';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { GAActions, GACategories } from '../../analytics';
import { handleGAEvent, isLoggedIn } from '../../helperFunctions';
import { loginOTP, loginUsingGoogle, requestOTP } from '../../store/actions/AuthActions';
import { updateSectionStatus } from '../../store/actions/AssessmentActions';
import { setCurrentPage } from '../../store/actions/commonActions';
import * as actions from '../../store/actions/InviteActions';
import { AssessmentStatus, InviteStatus } from '../../store/reducers/InviteReducer';
import determineWorkflow from '../../Utils/determineWorkflow';
import EmailForm from './EmailForm';
import styles from './InviteContainer.scss';
import InviteHeader from './InviteHeader';
import OTPForm from './OTPForm';
import TestDetails from './TestDetails';
import TestInstructions from '../../components/Instructions/TestInstructions';
import DynamicTestInstruction from '../../components/DynamicTestInstruction/DynamicTestInstruction';
import { showModal } from '../../store/actions/AssessmentActions';
import Lock from '../../assets/images/Lock.png';

const InviteUIContainer = () => {
  const { t } = useTranslation('auth');
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const [otp, setOtp] = useState('');
  const [error, setError] = useState(false);

  const query = new URLSearchParams(location.search);
  const invite_id = query.get('invite_id');
  const hash_value = query.get('hash_value');
  const company_id = query.get('company_id');

  const {
    assessment_id,
    status,
    assessment_status,
    user_id,
    company_details,
    test_id,
    test_title,
    test_time,
    test_num_problems,
    login_token,
    email_id,
    isOtp,
    problemId,
    test_settings,
    sections,
    problems,
    mcqIds,
    currentSectionId,
  } = useSelector(state => ({
    assessment_id: state.invite.assessment_id,
    status: state.invite.status,
    assessment_status: state.invite.assessment_status,
    user_id: state.auth.user_id,
    company_details: state.auth.company_details,
    test_id: state.invite.test_id,
    test_title: state.invite.test_title,
    test_time: state.invite.test_time,
    test_num_problems: state.invite.test_num_problems,
    login_token: state.invite.login_token,
    email_id: state.invite.email_id,
    isOtp: state.auth.otp,
    problemId: state.assessment?.problems[0]?.id,
    test_settings: state.assessment.test_settings,
    sections: state.assessment.sections,
    problems: state.assessment.problems,
    mcqIds: state.assessment.mcqIds,
    currentSectionId: state.assessment.currentSectionId,
  }));

  useEffect(() => {
    dispatch(actions.fetchInvite(invite_id, hash_value, company_id));
    dispatch(setCurrentPage('RTP'));
  }, [dispatch, invite_id, hash_value, company_id]);

  useEffect(() => {
    if (
      isLoggedIn(user_id) &&
      status === InviteStatus.ACTIVE &&
      !assessment_id &&
      test_id &&
      invite_id
    ) {
      dispatch(actions.fetchAssesmentForInvite(test_id, invite_id));
    }
  }, [user_id, status, assessment_id, test_id, invite_id, dispatch]);

  useEffect(() => {
    if (isOtp.status) {
      setError(isOtp.status === 'invalid' || isOtp.status === 'expired');
    }
  }, [isOtp.status]);

  const handleGoogleResponse = response => {
    if (response.error === 'idpiframe_initialization_failed') {
      let url, option;
      if (isChrome) {
        url = 'chrome://settings/cookies';
        option = t('invite.cookies.chrome_option');
      } else if (isFirefox) {
        url = 'about:preferences#privacy';
        option = t('invite.cookies.firefox_option');
      }

      dispatch(
        showModal({
          type: 'error',
          title: t('invite.cookies.title'),
          content: (
            <div>
              <p>{t('invite.cookies.description')}</p>
              {url ? (
                <div>
                  <br />
                  <p>{t('invite.cookies.paste_url')}</p>
                  <p>
                    <strong>{url}</strong>
                  </p>
                  {option}
                  <br />
                </div>
              ) : (
                <div>
                  <br />
                </div>
              )}
              <p>{t('invite.cookies.footer')}</p>
            </div>
          ),
          okText: t('invite.cookies.refresh'),
          onOk: () => {
            window.location.reload(false);
          },
        }),
      );
    }

    const payload = {
      provider: 'google',
      company_id,
      code: response.code,
    };
    dispatch(loginUsingGoogle(payload));
  };

  const handleStartTest = () => {
    dispatch(actions.startTest(test_id, invite_id));
  };

  const handleResumeTest = () => {
    if (test_settings.enable_screen_and_camera) {
      history.push(`/recording/${assessment_id}`);
    } else {
      determineWorkflow({
        sections: sections,
        problems: problems,
        mcqIds: mcqIds,
        assessmentId: assessment_id,
        routerHistory: history,
        shouldRoute: true,
        currentSectionId: currentSectionId,
      });
    }
  };

  const handleRequestOTP = values => {
    const enteredEmail = values.email;
    if (enteredEmail === email_id) {
      dispatch(requestOTP(email_id, login_token));
    } else {
      Modal.error({
        title: t('invite.email_mismatch.title'),
        content: t('invite.email_mismatch.message'),
        okText: t('invite.email_mismatch.ok'),
      });
    }
  };

  const handleOtpChange = value => setOtp(value);
  const handleOTPSubmit = () => dispatch(loginOTP(email_id, otp));

  let ctaButton;
  if (isLoggedIn(user_id)) {
    if (assessment_id && assessment_status === AssessmentStatus.InProgress) {
      ctaButton = (
        <Button
          onClick={handleGAEvent(
            handleResumeTest,
            GACategories.assessment,
            GAActions.resumeTest,
            null,
            () => ({ assessment_id, test_id, invite_id }),
          )}
          size="large"
          type="primary"
        >
          {t('invite.resume_test')}
        </Button>
      );
    } else if (!assessment_id) {
      ctaButton = (
        <Button
          onClick={handleGAEvent(
            handleStartTest,
            GACategories.assessment,
            GAActions.startTest,
            null,
            () => ({ test_id, invite_id }),
          )}
          size="large"
          type="primary"
        >
          {t('invite.start_test')}
        </Button>
      );
    }
  }

  const renderContent = () => {
    switch (isOtp.status) {
      case 'send':
      case 'invalid':
      case 'expired':
        return (
          <div>
            <p style={{ fontSize: '24px', fontWeight: '700' }}>{t('invite.otp.title')}</p>
            <br />
            <p style={{ fontSize: '14px' }}>
              {t('invite.otp.subtitle')}
              <br />
              {email_id}
            </p>
            <OTPForm
              otp={otp}
              onChange={handleOtpChange}
              onSubmit={handleOTPSubmit}
              error={error}
              disabled={otp.length !== 6}
              email_id={email_id}
              message={isOtp.message}
            />
          </div>
        );

      case 'idle':
        return (
          <>
            <p style={{ fontSize: '14px', marginBottom: 12 }}>{t('invite.description')}</p>
            <p style={{ display: 'flex', marginLeft: 14, fontSize: 14, marginBottom: 2 }}>
              {t('invite.email_label')}
            </p>
            <EmailForm
              email_id={email_id}
              onRequestOTP={handleRequestOTP}
              onGoogleResponse={handleGoogleResponse}
            />
          </>
        );

      case 'Account Locked':
        return (
          <div>
            <img
              src={Lock}
              alt={t('invite.account_locked.alt')}
              style={{ width: 50, height: 50, marginBottom: 20 }}
            />
            <p style={{ fontWeight: 700, fontSize: 24 }}>{t('invite.account_locked.title')}</p>
            <p style={{ fontSize: 14, color: 'red', padding: 0 }}>
              {t('invite.account_locked.message')}
            </p>
            <span style={{ fontSize: 14, color: 'red' }}>
              {t('invite.account_locked.retry', { duration: '60 mins' })}
            </span>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className={styles['l-invite-wrapper']}>
      <InviteHeader company={company_details} />

      {status === InviteStatus.ACTIVE && assessment_status !== AssessmentStatus.Completed ? (
        <div
          className={`${
            !isLoggedIn(user_id) ? styles['c-invite'] : styles['c-invite-instructions']
          }`}
        >
          {isOtp.status ? (
            ''
          ) : (
            <TestDetails
              title={test_title}
              numProblems={test_num_problems}
              time={test_time}
            />
          )}

          {!isLoggedIn(user_id) ? (
            <div className={styles['c-invite__test-instructions']}>{renderContent()}</div>
          ) : (
            <div style={{ width: 800 }}>
              <DynamicTestInstruction ctaButton={ctaButton} />
            </div>
          )}
        </div>
      ) : (
        <div className={styles['c-invite__error-msg']}>{ctaButton}</div>
      )}
    </div>
  );
};

export default InviteUIContainer;
